import Component from '@bythepixel/component-loader';
import {
  lazyLoadIframeInModal,
} from '@Utility';
import GlideSlider from '../base';

// Refer to https://glidejs.com/docs/options/
const glideOptions = {
  type: 'carousel',
  dragThreshold: false,
  focusAt: 0,
  perTouch: 1,
  perView: 1,
};

// When there is a video and less than three media files, there is a bug with the carousel type
// that prevents the video element from being observed, therefore never getting loaded
// The slider type does not have this bug
const glideOptionsAlternate = {
  type: 'slider',
  dragThreshold: false,
  focusAt: 0,
  perTouch: 1,
  perView: 1,
};

const cssClasses = {
  controlsActiveState: 'section-media-gallery-thumbnails__controls--active',
  triggerActiveState: 'section-media-gallery-thumbnails__trigger--active',
};

const mobileSelectors = {
  leftArrowSelector: '[data-media-gallery-mobile-arrow-left]',
  rightArrowSelector: '[data-media-gallery-mobile-arrow-right]',
  sliderSelector: '[data-media-gallery-mobile]',
  slideSelector: '[data-media-gallery-mobile-slide]',
};

const modalSliderSelectors = {
  buttonSelector: '[data-photo-button]',
  leftArrowSelector: '[data-media-gallery-slider-arrow-left]',
  modalSelector: '[data-comp-modal]',
  rightArrowSelector: '[data-media-gallery-slider-arrow-right]',
  slideCaptionSelector: '[data-media-gallery-slide-caption]',
  sliderSelector: '[data-media-gallery-modal]',
  slideSelector: '[data-media-gallery-slider-slide]',
  triggerSelector: '[data-media-gallery-modal-trigger]',
};

const thumbnailSelectors = {
  controlsSelector: '[data-media-gallery-slider-thumbnails-controls]',
  leftArrowSelector: '[data-media-gallery-slider-thumbnails-arrow-left]',
  rightArrowSelector: '[data-media-gallery-slider-thumbnails-arrow-right]',
  sliderSelector: '[data-media-gallery-slider-thumbnails]',
};

export default class SectionMediaGallerySlider extends Component {
  static selector = '[data-comp-media-gallery]';

  constructor(containerEl) {
    super(containerEl);
    // Modal slider
    this.modalSliderEl = this.$container.querySelector(modalSliderSelectors.sliderSelector);
    this.slideCount = this.$container.querySelectorAll(modalSliderSelectors.slideSelector).length;
    this.modalTriggers = this.$container.querySelectorAll(modalSliderSelectors.triggerSelector);
    this.modalPhotoButton = this.$container.querySelector(modalSliderSelectors.buttonSelector);
    this.video = this.$container.querySelector('[data-modal-iframe-src]');

    // Thumbnail slider
    this.thumbnailSlider = this.$container.querySelector(thumbnailSelectors.sliderSelector);
    if (this.thumbnailSlider) {
      this.thumbnailControls = this.thumbnailSlider.querySelector(thumbnailSelectors.controlsSelector);
      this.leftThumbnailArrow = this.thumbnailSlider.querySelector(thumbnailSelectors.leftArrowSelector);
      this.rightThumbnailArrow = this.thumbnailSlider.querySelector(thumbnailSelectors.rightArrowSelector);
    }

    if (!this.modalSliderEl) {
      this.modalTriggerEvents();
      return;
    }

    // Mobile slider
    this.mobileSliderEl = this.$container.querySelector(mobileSelectors.sliderSelector);
    if (this.mobileSliderEl && window.matchMedia('(max-width: 767px)').matches) {
      this.mobileSliderBase = new GlideSlider(
        this.mobileSliderEl,
        glideOptions,
        mobileSelectors,
      );
      this.mobileGlide = this.mobileSliderBase.getGlideSlider();
    }

    // Modal slider
    this.updateGlide = true;
    this.isAlternateSlider = this.$container.hasAttribute('data-media-gallery-alternate');
    this.modalSliderGlideOptions = this.isAlternateSlider ? glideOptionsAlternate : glideOptions;
    this.modalSliderBase = new GlideSlider(
      this.modalSliderEl,
      this.modalSliderGlideOptions,
      modalSliderSelectors,
    );
    this.modalGlide = this.modalSliderBase.getGlideSlider();
    this.modal = this.$container.querySelector(modalSliderSelectors.modalSelector);
    this.modalCaption = this.$container.querySelector(modalSliderSelectors.slideCaptionSelector);

    this.glideEvents();
    this.modalTriggerEvents();
    this.modalEvents();
    this.orientationChangeEvent();

    this.initializeYTPlayer();
  }

  glideEvents() {
    this.modalGlide.on('resize', () => {
      this.updateGlide = true;
    });

    this.modalGlide.on('update', () => {
      // When there is a video and there is three or less media files, glidejs creates a clone of the video slide
      // which prevents the youtube player in the microModalAdpt class from working
      // To get around this, we need to remove the id from the video slide clone
      if (this.isAlternateSlider) {
        const videoSlideClone = this.$container.querySelector('.section-media-gallery__slide--video.glide__slide--clone');
        if (!videoSlideClone) {
          return;
        }

        const video = videoSlideClone.querySelector('iframe');
        video.removeAttribute('id');
      }

      this.styleThumbnail(this.thumbnailSlider.querySelector(`[data-media-gallery-modal-trigger="${this.modalGlide.index}"]`));
    });

    // Run these events before transitioning the slide, but after calculating the new slide index
    this.modalGlide.on('run', () => {
      const { index } = this.modalGlide;
      const thumbnail = this.thumbnailSlider.querySelector(`[data-media-gallery-modal-trigger="${index}"]`);
      this.styleThumbnail(thumbnail);
      this.updateCaption(index);
      this.videoEvents();

      if (this.mobileGlide) {
        this.mobileGlide.go(`=${index}`);
      }
    });

    if (this.mobileGlide) {
      this.mobileGlide.on('run.after', () => {
        const { index } = this.mobileGlide;

        if (this.modalGlide && this.modalPhotoButton) {
          this.modalPhotoButton.dataset.mediaGalleryModalTrigger = index;
        }
      });
    }

    // Initialize slider, events must be declared before initializing the slider
    this.modalGlide.mount();

    if (this.mobileGlide && window.matchMedia('(max-width: 767px)').matches) {
      this.mobileGlide.mount();
    }
  }

  initializeYTPlayer() {
    if (!this.video) {
      return;
    }

    if (window.YT) {
      window.YT.ready(() => {
        lazyLoadIframeInModal(this.video, () => {
          const player = new window.YT.Player(this.video);

          window.MediaGalleryYoutubeVideo = player;
          window.YoutubePlayers.push(player);
        });
      });
    }
  }

  modalEvents() {
    if (!this.modal) {
      return;
    }

    // Since this slider is in a modal and since the images are lazy loaded, the widths do not get calculated correctly on page load
    // When the modal is first opened, this updates the glide instance with the current options
    // to recalculate the widths correctly
    this.modal.addEventListener('animationstart', () => {
      if (this.updateGlide) {
        this.modalGlide.update(this.modalSliderGlideOptions);
        this.thumbnailSliderEvents();
        this.updateGlide = false;
      }
    });
  }

  modalTriggerEvents() {
    // When there's only one image in the modal gallery, this styles the thumbnail
    if (!this.modalSliderEl && this.slideCount === 1) {
      this.modalTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const thumbnail = this.thumbnailSlider.querySelector('[data-media-gallery-modal-trigger="0"]');
          this.styleThumbnail(thumbnail);
        });
      });

      return;
    }

    if (this.modalTriggers.length === 0) {
      return;
    }

    // When there are multiple images, this navigates the modal to the appropriate image
    this.modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const index = trigger.dataset.mediaGalleryModalTrigger;
        this.modalGlide.go(`=${index}`);
      });
    });
  }

  // This is primarily needed for mobile
  // When the orientation changes, this sets the main slide widths correctly
  orientationChangeEvent() {
    const orientationChange = window.matchMedia('(orientation: landscape)');
    orientationChange.addEventListener('change', () => {
      setTimeout(() => {
        this.modalGlide.update(this.modalSliderGlideOptions);

        const sliderWidth = this.modalSliderBase.getSliderWidth();
        this.modalSliderBase.adjustSlideWidth(`${sliderWidth}px`);
      }, 500);
    });
  }

  videoEvents() {
    if (!this.video) {
      return;
    }

    if (window.MediaGalleryYoutubeVideo
      && typeof window.MediaGalleryYoutubeVideo.seekTo === 'function'
      && typeof window.MediaGalleryYoutubeVideo.pauseVideo === 'function'
    ) {
      window.MediaGalleryYoutubeVideo.seekTo(0);
      window.MediaGalleryYoutubeVideo.pauseVideo();
    }
  }

  styleThumbnail(thumbnail) {
    if (this.modalTriggers.length === 0 || !thumbnail) {
      return;
    }

    this.modalTriggers.forEach(trigger => {
      trigger.classList.remove(cssClasses.triggerActiveState);
    });

    thumbnail.classList.add(cssClasses.triggerActiveState);
    thumbnail.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
    });
  }

  thumbnailSliderEvents() {
    if (!this.thumbnailControls) {
      return;
    }

    // Hide thumbnail controls if width is less than the window width
    // This means that all the thumbnail images fit in the window
    if (this.thumbnailControls.offsetWidth < window.innerWidth) {
      this.thumbnailControls.style.display = 'none';
      return;
    }

    if (this.thumbnailControls.offsetWidth >= window.innerWidth) {
      this.thumbnailControls.style.display = 'block';
    }

    if (!this.leftThumbnailArrow || !this.rightThumbnailArrow) {
      return;
    }

    // Previous thumbnail navigation
    this.leftThumbnailArrow.addEventListener('click', () => {
      // If index is on 0, and user clicks the previous button
      // This will update the thumbnail slide to go to the last slide
      const index = this.modalGlide.index === 0 ? this.slideCount - 1 : this.modalGlide.index - 1;
      this.modalGlide.go(`=${index}`);
    });

    // Next thumbnail navigation
    this.rightThumbnailArrow.addEventListener('click', () => {
      // If index is on the last slide, and user clicks the next button
      // This will update the thumbnail slide to go to the first slide
      const index = this.modalGlide.index === (this.slideCount - 1) ? 0 : this.modalGlide.index + 1;
      this.modalGlide.go(`=${index}`);
    });
  }

  updateCaption(index) {
    if (!this.modalCaption) {
      return;
    }

    this.modalCaption.innerText = `${index + 1} of ${this.slideCount}`;
  }
}
